/**
 * Servicio para guardar conversaciones en AWS Lambda
 * Usa FinalTasks del proyecto original LearnEnglishWeb
 * Basado en AWSlambdaCall.cs
 */

import { ConversationData, Conversation, UserSummary, UserCharacterization } from '../types';
import configService from './configService';
import interactionTimeService from './interactionTimeService';
import authService from './authService';
import gamificationService from './gamificationService';
import mentorAiService from './mentorAi/mentorAiService';
// lambdaService ya no se usa - payload simplificado migrado desde AF

// Declaración para función JavaScript externa (si existe)
declare global {
  interface Window {
    LambdaFunction?: (url: string, jsonData: string) => void;
  }
}

export class ConversationSaverService {
  // Endpoint de Lambda migrado desde LearnEnglishWeb (Unity)
  // Usa FinalTasks del proyecto original
  private linkPrueba = import.meta.env.VITE_LAMBDA_CONVERSATION_ENDPOINT || 
    'https://tqj6m7oaxi.execute-api.us-east-1.amazonaws.com/FinalTasks';
  private internalInteractionId: string = '';
  private numeroMensajesToSend: number = 0;
  private currentMode: string | null = null;
  private currentPersonality: string | null = null;
  private currentAccent: string | null = null;
  private currentCharacter: string | null = null;
  private textMessageCount: number = 0;
  private audioMessageCount: number = 0;
  private notValidMessageCount: number = 0; // Contador de mensajes no válidos (vacíos)
  private pendingFeedback: { rating: number; feedback_text?: string; created_at: string } | null = null;
  
  // Rastreo de tiempo del chat (abierto/cerrado)
  private chatOpenStartTime: number | null = null;
  private chatClosedStartTime: number | null = null;
  private totalChatOpenTime: number = 0; // Tiempo acumulado con chat abierto (time_text)
  private totalChatClosedTime: number = 0; // Tiempo acumulado con chat cerrado (time_no_text)
  private isChatCurrentlyOpen: boolean = false;

  // Rastreo de tiempo muerto (después de que el asistente termina de hablar)
  private idleTimer: NodeJS.Timeout | null = null; // Timer de 15 segundos antes de empezar a contar
  private idleStartTime: number | null = null; // Timestamp cuando empezó el tiempo muerto (después de 15s)
  private totalIdleTime: number = 0; // Tiempo muerto acumulado en segundos
  private readonly IDLE_THRESHOLD = 15; // Segundos de espera antes de empezar a contar tiempo muerto

  constructor() {
    // Generar ID único para esta interacción
    this.internalInteractionId = this.generateGuid();
  }

  /**
   * Establecer configuración actual del usuario (modo, personalidad, acento)
   */
  setCurrentConfiguration(mode: string, personality: string, accent: string, character?: string): void {
    this.currentMode = mode;
    this.currentPersonality = personality;
    this.currentAccent = accent;
    this.currentCharacter = character || null;
  }

  /**
   * Obtener el ID de la interacción actual
   */
  getCurrentInteractionId(): string {
    return this.internalInteractionId;
  }

  /**
   * Obtener el nombre del bot según el modo
   */
  private getBotName(mode: string | null): string {
    // Usar botName de mentorAiService (igual que Unity usa JsonRequest.botName)
    // En Unity: botName = "Coach" o "learnAssistant" (NO "Nia" o "Ian")
    // "Nia"/"Ian" se usan solo en el prompt, no en el payload de Lambda
    const mentorState = mentorAiService.getState();
    if (mentorState.botName) {
      return mentorState.botName; // "Coach" o "learnAssistant"
    }
    
    // Fallback si no está disponible
    if (!mode) return 'learnAssistant';
    
    const nameMap: Record<string, string> = {
      'conversacional': 'learnAssistant',
      'estudio': 'learnAssistant',
      'examen': 'learnAssistant',
      'actividades-docente': 'learnAssistant',
      'actividades-estudiante': 'learnAssistant',
    };
    
    return nameMap[mode] || 'learnAssistant';
  }

  /**
   * Incrementar contador de mensajes de texto
   */
  incrementTextCount(): void {
    this.textMessageCount++;
  }

  /**
   * Incrementar contador de mensajes de audio
   */
  incrementAudioCount(): void {
    this.audioMessageCount++;
  }

  /**
   * Incrementar contador de mensajes no válidos (vacíos)
   */
  incrementNotValidCount(): void {
    this.notValidMessageCount++;
  }

  /**
   * Establecer feedback pendiente para incluir en el payload cuando se guarde
   */
  setPendingFeedback(feedback: { rating: number; feedback_text?: string; created_at: string }): void {
    this.pendingFeedback = feedback;
  }

  /**
   * Obtener feedback pendiente
   */
  getPendingFeedback(): { rating: number; feedback_text?: string; created_at: string } | null {
    // Primero intentar desde memoria
    if (this.pendingFeedback) {
      return this.pendingFeedback;
    }
    
    // Si no está en memoria, intentar desde localStorage
    try {
      const storageKey = `feedback_${this.internalInteractionId}`;
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      // console.error('[ConversationSaver] Error al leer feedback desde localStorage:', error);
    }
    
    return null;
  }

  /**
   * Limpiar feedback pendiente después de guardar exitosamente
   */
  private clearPendingFeedback(): void {
    this.pendingFeedback = null;
    try {
      const storageKey = `feedback_${this.internalInteractionId}`;
      localStorage.removeItem(storageKey);
    } catch (error) {
      // console.error('[ConversationSaver] Error al limpiar feedback de localStorage:', error);
    }
  }

  /**
   * Resetear estado de conversación para nueva interacción
   * Genera nuevo internalInteractionId y resetea contadores
   */
  resetConversationState(): void {
    this.internalInteractionId = this.generateGuid();
    this.numeroMensajesToSend = 0;
    this.textMessageCount = 0;
    this.audioMessageCount = 0;
    this.notValidMessageCount = 0;
    // Resetear tiempo muerto
    this.stopIdleTimer();
    this.totalIdleTime = 0;
    // Limpiar feedback pendiente
    this.clearPendingFeedback();
  }

  /**
   * Registrar cambio de visibilidad del chat
   * @param isVisible true si el chat se abrió, false si se cerró
   */
  setChatVisibility(isVisible: boolean): void {
    const now = Date.now();
    
    if (isVisible && !this.isChatCurrentlyOpen) {
      // Chat se acaba de abrir
      // Si había estado cerrado, acumular ese tiempo
      if (this.chatClosedStartTime !== null) {
        const closedDuration = Math.floor((now - this.chatClosedStartTime) / 1000); // en segundos
        this.totalChatClosedTime += closedDuration;
        this.chatClosedStartTime = null;
      }
      this.chatOpenStartTime = now;
      this.isChatCurrentlyOpen = true;
    } else if (!isVisible && this.isChatCurrentlyOpen) {
      // Chat se acaba de cerrar
      // Acumular el tiempo que estuvo abierto
      if (this.chatOpenStartTime !== null) {
        const openDuration = Math.floor((now - this.chatOpenStartTime) / 1000); // en segundos
        this.totalChatOpenTime += openDuration;
        this.chatOpenStartTime = null;
      }
      this.chatClosedStartTime = now;
      this.isChatCurrentlyOpen = false;
    }
  }

  /**
   * Obtener tiempo total con chat abierto (time_text)
   */
  getTimeText(): number {
    let currentOpenTime = 0;
    if (this.isChatCurrentlyOpen && this.chatOpenStartTime !== null) {
      // Si el chat está abierto ahora, agregar el tiempo desde que se abrió
      currentOpenTime = Math.floor((Date.now() - this.chatOpenStartTime) / 1000);
    }
    return this.totalChatOpenTime + currentOpenTime;
  }

  /**
   * Obtener tiempo total con chat cerrado (time_no_text)
   */
  getTimeNoText(): number {
    let currentClosedTime = 0;
    if (!this.isChatCurrentlyOpen && this.chatClosedStartTime !== null) {
      // Si el chat está cerrado ahora, agregar el tiempo desde que se cerró
      currentClosedTime = Math.floor((Date.now() - this.chatClosedStartTime) / 1000);
    }
    return this.totalChatClosedTime + currentClosedTime;
  }

  /**
   * Iniciar timer de tiempo muerto cuando el asistente termina de responder
   * Espera 15 segundos y luego empieza a contar tiempo muerto
   */
  startIdleTimer(): void {
    // Limpiar timer anterior si existe
    this.stopIdleTimer();


    // Iniciar timer de 15 segundos
    this.idleTimer = setTimeout(() => {
      // Han pasado 15 segundos sin que el usuario hable
      this.idleStartTime = Date.now();
    }, this.IDLE_THRESHOLD * 1000);
  }

  /**
   * Detener el contador de tiempo muerto cuando el usuario envía un mensaje
   */
  stopIdleTimer(): void {
    // Limpiar timer de espera si existe
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }

    // Si estaba contando tiempo muerto, acumularlo y detener
    if (this.idleStartTime !== null) {
      const idleDuration = Math.floor((Date.now() - this.idleStartTime) / 1000);
      this.totalIdleTime += idleDuration;
      this.idleStartTime = null;
    }
  }

  /**
   * Obtener tiempo muerto acumulado (en segundos)
   * Incluye el tiempo actual si está contando
   */
  getIdleTime(): number {
    let currentIdleTime = 0;
    if (this.idleStartTime !== null) {
      // Si está contando tiempo muerto ahora, agregar el tiempo desde que empezó
      currentIdleTime = Math.floor((Date.now() - this.idleStartTime) / 1000);
    }
    return this.totalIdleTime + currentIdleTime;
  }

  /**
   * Concatenar mensajes assistant consecutivos
   * Concatena chunks de la misma respuesta normalmente
   * EXCEPTO cuando un mensaje tiene afterInvalidMessage: true (viene después de un mensaje no válido)
   */
  private concatenateAssistantMessages(messages: Array<{ role: string; content: string; afterInvalidMessage?: boolean }>): Array<{ role: string; content: string }> {
    return messages.reduce((acc: Array<{ role: string; content: string }>, message, index) => {
      if (index === 0) {
        // Primer mensaje siempre se agrega
        acc.push({ role: message.role, content: message.content });
      } else {
        const previousMessage = acc[acc.length - 1];
        
        // Si ambos son assistant, verificar si deben concatenarse
        if (message.role === 'assistant' && previousMessage.role === 'assistant') {
          // Si el mensaje actual viene después de un mensaje no válido, NO concatenar
          if (message.afterInvalidMessage) {
            // Es una nueva respuesta después de un mensaje no válido
            acc.push({ role: message.role, content: message.content });
          } else {
            // Concatenar normalmente (chunks de la misma respuesta)
            previousMessage.content = `${previousMessage.content}\n\n${message.content}`;
          }
        } else {
          // Si es user o es el primer assistant después de un user, agregar normalmente
          acc.push({ role: message.role, content: message.content });
        }
      }
      return acc;
    }, []);
  }

  /**
   * Guardar datos de conversación
   */
  async guardarData(finalMessages: Array<{ role: string; content: string; afterInvalidMessage?: boolean }>): Promise<void> {
    if (finalMessages.length <= this.numeroMensajesToSend) {
      return; // No hay nuevos mensajes para enviar
    }

    try {
      // Concatenar mensajes assistant consecutivos antes de procesar
      const processedMessages = this.concatenateAssistantMessages(finalMessages);
      
      // IMPORTANTE: Enviar TODO el historial completo, no solo los mensajes nuevos
      // Esto asegura que cuando Lambda actualice la conversación, tenga todo el historial
      // Formato compatible con Unity: "Prompt" para user, "Output" para assistant
      const listaMensajesCompleta = processedMessages
        .map((message) => {
          // Mapear roles al formato de Unity
          const roleKey = message.role === 'user' ? 'Prompt' : 'Output';
          return {
            [roleKey]: message.content,
          };
        });

      // Solo los mensajes nuevos para procesamiento (si es necesario)
      const listaMensajesNuevos = processedMessages
        .slice(this.numeroMensajesToSend)
        .map((message) => ({
          [message.role]: message.content,
        }));

      if (listaMensajesNuevos.length > 0) {
        // Enviar TODO el historial completo, no solo los nuevos
        await this.estructuraToSend(listaMensajesCompleta);
        this.numeroMensajesToSend = finalMessages.length;
        
        // NO registrar interacciones aquí - total_interactions cuenta conversaciones, no interacciones individuales
        // El conteo de conversaciones se hace en updateUserSummary cuando se guarda la conversación
      }
    } catch (error) {
      // console.error('Error al guardar datos:', error);
    }
  }

  private async estructuraToSend(
    listaMensajesEnviar: Array<{ [key: string]: string }>
  ): Promise<void> {
    const config = configService.getConfig();
    const totalTime = Math.round(interactionTimeService.getTimeElapsed());
    // const updateTimeID = interactionTimeService.getUpdateTimeID() || '';

    // Usar firstname y lastname si están disponibles, sino parsear de fullName
    let firstname = config.userFirstname;
    let lastname = config.userLastname;
    
    // Si no están disponibles, intentar parsear de fullName
    if (!firstname && !lastname && config.userFullName) {
      const parsed = this.parseFullName(config.userFullName);
      firstname = parsed.firstname;
      lastname = parsed.lastname;
    }
    
    // Generar fullName como suma de firstname + lastname
    const fullName = firstname && lastname 
      ? `${firstname} ${lastname}` 
      : firstname || config.userFullName || '';

    // Payload completo compatible con Unity AWSlambdaCall.cs
    // Incluye todos los campos que la Lambda espera según estructuraCoach()
    const mentorState = mentorAiService.getState();
    const updateTimeID = interactionTimeService.getUpdateTimeID();
    const timeText = this.getTimeText();
    const timeNoText = this.getTimeNoText();
    const timeDead = this.getIdleTime();
    
    // Calcular tiempos adicionales (valores por defecto si no están implementados)
    const timeSlides = Math.max(0, totalTime - timeNoText); // Aproximación: tiempo total menos tiempo sin texto
    const timeMindMaps = 0; // No implementado aún
    const timeZoomScreen = 0; // No implementado aún
    
    // Obtener fecha UTC ajustada a UTC-5 (como Unity)
    const nowUTC = new Date();
    const utcMinus5 = new Date(nowUTC.getTime() - (5 * 60 * 60 * 1000));
    
    const lambdaPayload = {
      bot_name: this.getBotName(this.currentMode),
      assistant_id: config.assistantID || '',
      canal: config.client || 'unity',
      internal_interaction_id: this.internalInteractionId,
      final_messages: listaMensajesEnviar,
      current_scene: this.getBotName(this.currentMode), // Mismo que bot_name
      access_token: config.refreshToken || config.accessToken || '', // refreshToken como en Unity
      QuestionsUrl: mentorState.QuestionsUrl || '',
      RefreshTokenUrl: mentorState.RefreshTokenUrl || '',
      StudentInformationUrl: mentorState.StudentInformationUrl || '',
      available: true,
      created: utcMinus5.toISOString(), // UTC-5 como Unity
      modified: utcMinus5.toISOString(), // UTC-5 como Unity
      student_id: config.userID || 0,
      customer_student_id: 0, // No implementado
      course_slug: '', // No implementado
      course_id: 0, // No implementado
      course_name: '', // No implementado
      student_name: fullName,
      student_email: config.email || '',
      student_firstname: firstname || '',
      student_lastname: lastname || '',
      section_id: 0, // No implementado
      section_name: '', // No implementado
      reminder: '', // No implementado
      time_slides: Math.round(timeSlides),
      time_mind_maps: Math.round(timeMindMaps),
      time_text: timeText,
      time_no_text: timeNoText,
      time_zoom_screen: Math.round(timeZoomScreen),
      time_total: totalTime,
      time_dead: timeDead, // Tiempo muerto separado
      gamification_points_section: 0, // No implementado aún (Gamificacion.PuntuacionGamificacion en Unity)
      ResumenSumarice: configService.getSummaryPrevAssistant(), // Resumen de la interacción previa
      updateTime_url: mentorState.UpdateTimeUrl || '',
      updateTime_id: updateTimeID || '',
      global_time: Math.round(interactionTimeService.getTimeElapsed()), // Timer.timeElapse en Unity
      // Campos adicionales del proyecto actual
      interactions_text_count: this.textMessageCount,
      interactions_audio_count: this.audioMessageCount,
      interactions_not_valid_count: this.notValidMessageCount,
      mode: this.currentMode || undefined,
      personality: this.currentPersonality || undefined,
      accent: this.currentAccent || undefined,
      // Campos de feedback (si existen)
      feedback_rating: this.getPendingFeedback()?.rating || undefined,
      feedback_text: this.getPendingFeedback()?.feedback_text || undefined,
      feedback_created_at: this.getPendingFeedback()?.created_at || undefined,
    };
    
    // Convertir a JSON para el log (síncrono)
    const jsonLambdaPayload = JSON.stringify(lambdaPayload, null, 2);
    
    // Imprimir payload ANTES de enviar (síncrono, se ejecuta inmediatamente)
    
    // El objeto questions se eliminó porque ya no se usa

    try {
      // Enviar a Lambda (Lambda guarda directamente en DynamoDB)
      this.sendToLambdaAndProcessResponse(this.linkPrueba, jsonLambdaPayload).catch(error => {
        // console.error('[ConversationSaver] Error en procesamiento asíncrono de Lambda:', error);
      });
    } catch (error) {
      // console.error('Error al enviar datos:', error);
    }
  }


  /**
   * Parsear nombre completo en firstname y lastname
   * Si no hay apellido, lastname será vacío
   */
  private parseFullName(fullName: string | undefined): { firstname: string; lastname: string } {
    if (!fullName || fullName.trim() === '') {
      return { firstname: '', lastname: '' };
    }
    
    const parts = fullName.trim().split(/\s+/);
    if (parts.length === 1) {
      return { firstname: parts[0], lastname: '' };
    }
    
    // Primera parte es el nombre, el resto es el apellido
    const firstname = parts[0];
    const lastname = parts.slice(1).join(' ');
    
    return { firstname, lastname };
  }

  /**
   * Guardar conversación (DEPRECATED)
   * DEPRECATED: Ya no se usa. Lambda guarda directamente en DynamoDB.
   */
  // @ts-ignore - Función deprecated, no se usa
  private async saveConversation_DEPRECATED(
    questions: ConversationData,
    finalMessages: Array<{ [key: string]: string }>
  ): Promise<void> {
    try {
      const config = configService.getConfig();
      if (!config.email) {
        return;
      }

      // Usar firstname y lastname desde config
      let firstname = config.userFirstname;
      let lastname = config.userLastname;
      
      // Si no están disponibles, parsear de fullName
      if (!firstname && !lastname && config.userFullName) {
        const parsed = this.parseFullName(config.userFullName);
        firstname = parsed.firstname;
        lastname = parsed.lastname;
      }
      
      // Generar fullName como suma de firstname + lastname
      const fullName = firstname && lastname 
        ? `${firstname} ${lastname}` 
        : firstname || config.userFullName || '';

      // Convertir final_messages al formato questions (JSONB)
      const questionsArray = finalMessages.map((msg) => {
        const role = Object.keys(msg)[0];
        const content = Object.values(msg)[0];
        return { role, content };
      });

      // Contar mensajes por tipo
      // Preparar conversación (legacy)
      const conversation: Partial<Conversation> = {
        internal_interaction_id: this.internalInteractionId,
        student_id: config.userID || undefined,
        student_email: config.email,
        student_firstname: firstname || undefined,
        student_lastname: lastname || undefined,
        student_name: fullName || undefined, // Mantener por compatibilidad
        assistant_id: config.assistantID || undefined,
        mode: this.currentMode || undefined,
        personality: this.currentPersonality || undefined,
        accent: this.currentAccent || undefined,
        questions: questionsArray.map(m => ({
          role: (m.role === 'user' || m.role === 'assistant') ? m.role : 'user' as 'user' | 'assistant',
          content: m.content
        })),
        total_messages_count: questionsArray.length,
        interactions_text_count: this.textMessageCount,
        interactions_audio_count: this.audioMessageCount,
        time_total: questions.time_total,
        time_no_text: questions.time_no_text,
        gamification_points_section: questions.gamification_points_section || 0,
        available: questions.available !== false,
      };

      // Guardado directo removido - Lambda maneja el guardado en DynamoDB
    } catch (error) {
      // console.error('[ConversationSaver] Error en función deprecated:', error);
      // No lanzar error para no interrumpir el flujo de Lambda
    }
  }

  /**
   * Actualizar resumen acumulativo del usuario
   * Esta función ya no hace nada
   */
  private async updateUserSummary(
    email: string,
    conversation: Partial<Conversation>
  ): Promise<void> {
    // No se actualiza resumen
    return;
    /* Código comentado - funcionalidad movida a Lambda/DynamoDB
    try {
      // Obtener resumen actual o crear uno nuevo
      let summary = getUserSummary(email);
      
      if (!summary) {
        summary = {
          email,
          total_time_seconds: 0,
          total_interactions: 0,
          total_points: 0,
          current_level: 1,
          current_streak: 0,
          unlocked_rewards: [],
          interactions_by_mode: {},
        };
      }

      // Actualizar campos acumulativos
      const previousInteractions = summary.total_interactions || 0;
      const newInteractions = previousInteractions + 1;
      
      const updates: Partial<UserSummary> = {
        total_time_seconds: (summary.total_time_seconds || 0) + (conversation.time_total || 0),
        total_interactions: newInteractions,
        total_points: (summary.total_points || 0) + (conversation.gamification_points_section || 0),
        last_interaction_date: new Date().toISOString(),
      };

      // Actualizar first_interaction_date si es la primera vez
      if (!summary.first_interaction_date) {
        updates.first_interaction_date = new Date().toISOString();
      }

      // NOTA: NO actualizar current_streak aquí porque:
      // 1. La racha se actualiza solo cuando se registra la conexión diaria (InitialScreen)
      // 2. La racha es independiente de las conversaciones - solo depende de si el usuario se conectó hoy
      // 3. Actualizar aquí causaría que la racha se recalcule innecesariamente en cada conversación

      // Actualizar nivel basado en tiempo e interacciones
      try {
        const levelProgress = await gamificationService.getLevelProgress(email);
        if (levelProgress) {
          updates.current_level = levelProgress.currentLevel;
        }
      } catch (levelError) {
        console.error('[ConversationSaver] Error al actualizar nivel:', levelError);
        // Si falla, mantener el nivel actual
        updates.current_level = summary.current_level || 1;
      }

      // Actualizar interactions_by_mode
      if (conversation.mode) {
        const modeCounts = summary.interactions_by_mode || {};
        modeCounts[conversation.mode] = (modeCounts[conversation.mode] || 0) + 1;
        updates.interactions_by_mode = modeCounts;

        // Actualizar preferred_mode si este modo tiene más interacciones
        const maxMode = Object.entries(modeCounts).reduce((a, b) => 
          modeCounts[a[0]] > modeCounts[b[0]] ? a : b
        );
        updates.preferred_mode = maxMode[0];
      }
      
      // Actualizar campos de comportamiento en user_characterization (diccionario flexible)
      const currentChar = summary.user_characterization || {};
      const updatedChar: UserCharacterization = { ...currentChar };
      
      // Actualizar preferred_modes (usar el actualizado en updates)
      if (conversation.mode) {
        updatedChar.preferred_modes = updates.interactions_by_mode || summary.interactions_by_mode || {};
      }
      
      // Actualizar preferred_personality
      if (conversation.personality) {
        updatedChar.preferred_personality = conversation.personality;
      }
      
      // Actualizar preferred_accent
      if (conversation.accent) {
        updatedChar.preferred_accent = conversation.accent;
      }
      
      // Calcular duración promedio de sesión
      const totalInteractions = (summary.total_interactions || 0) + 1;
      const totalTime = (summary.total_time_seconds || 0) + (conversation.time_total || 0);
      if (totalInteractions > 0 && totalTime > 0) {
        updatedChar.average_session_duration = Math.round(totalTime / totalInteractions);
      }
      
      // Calcular ratio texto/audio
      const textCount = conversation.interactions_text_count || 0;
      const audioCount = conversation.interactions_audio_count || 0;
      if (textCount + audioCount > 0) {
        updatedChar.text_vs_audio_ratio = textCount / (textCount + audioCount);
      }
      
      // Actualizar user_characterization (mantener todos los campos existentes + nuevos)
      updates.user_characterization = updatedChar;

      // Guardar actualizaciones
      await saveOrUpdateUserSummary({
        ...summary,
        ...updates,
      });
    } catch (error) {
      console.error('[ConversationSaver] Error al actualizar user_summary:', error);
    }
    */
  }

  /**
   * Actualizar conversación con resultados de Lambda (grade, insights, summary)
   * DEPRECATED: Ya no se usa.
   */
  async updateWithLambdaResults_DEPRECATED(
    grade?: string,
    insights?: string,
    summary?: string
  ): Promise<void> {
    // No se actualiza
  }
  
  /**
   * Consolidar insights de Lambda en la caracterización del usuario
   * Usa un diccionario flexible que permite agregar campos parametrizables
   */
  private async consolidateUserCharacterization(
    insights: string,
    currentChar: UserCharacterization | undefined
  ): Promise<UserCharacterization> {
    const char: UserCharacterization = currentChar || {};
    const metadata = char._metadata || {};
    
    // Parsear insights (texto en francés)
    // Formato esperado:
    // - Méthode d'apprentissage : ...
    // - Intérêts éducatifs : ...
    // - Intérêts personnels : ...
    // - Traits de personnalité : ...
    
    // Extraer método de aprendizaje
    const methodMatch = insights.match(/Méthode d'apprentissage\s*[:\-]\s*(.+?)(?:\n|$)/i);
    if (methodMatch) {
      const newMethod = methodMatch[1].trim();
      if (newMethod && newMethod !== char.learning_method) {
        // Si cambia significativamente, registrar en metadata
        if (char.learning_method) {
          if (!metadata.significant_changes) {
            metadata.significant_changes = [];
          }
          metadata.significant_changes.push({
            date: new Date().toISOString(),
            field: 'learning_method',
            old_value: char.learning_method,
            new_value: newMethod,
          });
        }
        char.learning_method = newMethod;
      }
    }
    
    // Extraer intereses educativos
    const educInterestsMatch = insights.match(/Intérêts éducatifs\s*[:\-]\s*(.+?)(?:\n|Intérêts personnels|Traits)/is);
    if (educInterestsMatch) {
      const interestsText = educInterestsMatch[1].trim();
      // Dividir por comas, puntos, o saltos de línea
      const interests = interestsText
        .split(/[,;•\n]/)
        .map(i => i.trim())
        .filter(i => i.length > 0);
      
      if (interests.length > 0) {
        const existingInterests = char.educational_interests || [];
        // Agregar nuevos intereses que no existan
        interests.forEach(interest => {
          if (!existingInterests.includes(interest)) {
            existingInterests.push(interest);
          }
        });
        char.educational_interests = existingInterests;
      }
    }
    
    // Extraer intereses personales
    const personalInterestsMatch = insights.match(/Intérêts personnels\s*[:\-]\s*(.+?)(?:\n|Traits|$)/is);
    if (personalInterestsMatch) {
      const interestsText = personalInterestsMatch[1].trim();
      const interests = interestsText
        .split(/[,;•\n]/)
        .map(i => i.trim())
        .filter(i => i.length > 0);
      
      if (interests.length > 0) {
        const existingInterests = char.personal_interests || [];
        interests.forEach(interest => {
          if (!existingInterests.includes(interest)) {
            existingInterests.push(interest);
          }
        });
        char.personal_interests = existingInterests;
      }
    }
    
    // Extraer rasgos de personalidad
    const traitsMatch = insights.match(/Traits de personnalité\s*[:\-]\s*(.+?)(?:\n|$)/is);
    if (traitsMatch) {
      const traitsText = traitsMatch[1].trim();
      const traits = traitsText
        .split(/[,;•\n]/)
        .map(t => t.trim())
        .filter(t => t.length > 0);
      
      if (traits.length > 0) {
        const existingTraits = char.personality_traits || [];
        traits.forEach(trait => {
          if (!existingTraits.includes(trait)) {
            existingTraits.push(trait);
          }
        });
        char.personality_traits = existingTraits;
      }
    }
    
    // Actualizar metadatos
    metadata.last_updated = new Date().toISOString();
    if (!metadata.first_insights_date) {
      metadata.first_insights_date = new Date().toISOString();
    }
    metadata.insights_update_count = (metadata.insights_update_count || 0) + 1;
    
    return {
      ...char,
      _metadata: metadata,
    };
  }
  
  /**
   * Actualizar un campo específico en la caracterización del usuario
   * Permite agregar campos parametrizables dinámicamente
   * Ejemplo: updateCharacterizationField('color_favorito', 'azul')
   */
  async updateCharacterizationField(field: string, value: any): Promise<void> {
    // No se actualiza caracterización
  }
  
  /**
   * Actualizar caracterización del usuario con nuevos insights
   * No se actualiza
   */
  private async updateUserCharacterization(insights: string): Promise<void> {
    // No se actualiza caracterización
  }
  
  /**
   * LambdaCall: Enviar datos usando sendBeacon con fallback a XMLHttpRequest
   * Basado en LambdaCall.jslib
   */
  // @ts-ignore - Función no usada actualmente
  private lambdaCall(url: string, data: string): void {
    const enableLogging = true;
    
    function log(message: string, isError: boolean = false): void {
      if (enableLogging) {
        if (isError) {
          // console.error(message);
        } else {
        }
      }
    }
    
    log(`Enviando datos a URL: ${url}`);
    log(`Datos: ${data.substring(0, 100)}...`);
    
    const sending = sessionStorage.getItem('sending');
    
    // Intentar usar sendBeacon primero (más confiable para cierre de navegador)
    if (navigator.sendBeacon && !sending) {
      sessionStorage.setItem('sending', '1');
      const blob = new Blob([data], { type: 'application/json' });
      if (navigator.sendBeacon(url, blob)) {
        log('Datos enviados exitosamente usando sendBeacon.');
        sessionStorage.removeItem('sending');
      } else {
        log('Error al enviar datos usando sendBeacon, usando XMLHttpRequest como fallback...', true);
        sessionStorage.removeItem('sending');
        this.sendWithXMLHttpRequest(url, data);
      }
    } else {
      log('sendBeacon no disponible o ya hay un envío en curso, usando XMLHttpRequest...');
      this.sendWithXMLHttpRequest(url, data);
    }
  }
  
  /**
   * Enviar a Lambda y procesar respuesta
   * Maneja respuesta que puede venir directamente o con 'body' como string JSON (desde API Gateway)
   */
  private async sendToLambdaAndProcessResponse(url: string, data: string): Promise<void> {
    try {
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: data,
      });
      

      if (!response.ok) {
        throw new Error(`Lambda responded with status ${response.status}`);
      }

      let result = await response.json();
      
      // Si la respuesta viene desde API Gateway, puede tener 'body' como string JSON
      if (result.body && typeof result.body === 'string') {
        try {
          result = JSON.parse(result.body);
        } catch (e) {
          // console.error('[ConversationSaver] Error al parsear body de respuesta:', e);
        }
      }
      
      // Procesar respuesta de Lambda (Lambda guarda directamente en DynamoDB)
      if (result.status === 'success') {
        // Lambda guarda la conversación directamente en DynamoDB
        // Ya no se actualiza user_summary desde aquí
        // console.log('[ConversationSaver] ✅ Conversación guardada exitosamente en DynamoDB vía Lambda');
        
        // Limpiar feedback pendiente después de guardar exitosamente
        this.clearPendingFeedback();
      } else if (result.status === 'error') {
        // console.error('[ConversationSaver] Lambda retornó error:', result.error);
      } else {
      }
    } catch (error) {
      // console.error('[ConversationSaver] Error al enviar a Lambda:', error);
      // No lanzar error para no interrumpir el flujo
    }
  }

  /**
   * Fallback: Enviar usando XMLHttpRequest
   */
  private sendWithXMLHttpRequest(url: string, data: string): void {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    
    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        // console.error('Error al enviar datos:', xhr.status, xhr.statusText);
      }
    };
    
    xhr.onerror = () => {
      // console.error('Error de red al enviar datos.');
    };
    
    xhr.send(data);
  }

  private generateGuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}

export const conversationSaverService = new ConversationSaverService();
export default conversationSaverService;


